import type { Group, Student, SeatingChart, SeatingAssignment } from "../types/models";

/**
 * Convert groups to CSV format
 */
export function groupsToCSV(groups: Group[], students: Student[]): string {
    const idToStudent = new Map(students.map(s => [s.id, s]));
    const lines: string[] = ["Group,Student Name,Skill,Tags"];

    for (const group of groups) {
        const groupName = group.name || `Group ${group.id.replace(/^g/, "")}`;
        for (const memberId of group.memberIds) {
            const student = idToStudent.get(memberId);
            if (!student) continue;
            const tags = student.tags.join("; ");
            lines.push(`"${groupName}","${student.name}",${student.skill},"${tags}"`);
        }
    }

    return lines.join("\n");
}

/**
 * Convert seating assignment to CSV format
 */
export function seatingToCSV(
    assignment: SeatingAssignment,
    chart: SeatingChart,
    students: Student[]
): string {
    const idToStudent = new Map(students.map(s => [s.id, s]));
    const lines: string[] = ["Table,Seat,Student Name,Skill,Tags"];

    for (const table of chart.tables) {
        for (let i = 0; i < table.seats.length; i++) {
            const seat = table.seats[i];
            if (!seat) continue;

            const studentId = assignment.seatAssignments.get(seat.id);
            const student = studentId ? idToStudent.get(studentId) : null;
            const studentName = student?.name ?? "(Empty)";
            const skill = student?.skill ?? "";
            const tags = student?.tags.join("; ") ?? "";

            lines.push(`"${table.name}",${i + 1},"${studentName}",${skill},"${tags}"`);
        }
    }

    return lines.join("\n");
}

/**
 * Convert roster to CSV format
 */
export function rosterToCSV(students: Student[]): string {
    const lines: string[] = ["Name,Skill,Tags,Absent"];

    for (const student of students) {
        const tags = student.tags.join("; ");
        lines.push(`"${student.name}",${student.skill},"${tags}",${student.absent ? "Yes" : "No"}`);
    }

    return lines.join("\n");
}

/**
 * Download content as a file
 */
export function downloadFile(content: string, filename: string, mimeType: string = "text/csv"): void {
    const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Download groups as CSV
 */
export function downloadGroupsCSV(groups: Group[], students: Student[], filename?: string): void {
    const csv = groupsToCSV(groups, students);
    const name = filename || `groups-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadFile(csv, name);
}

/**
 * Download seating as CSV
 */
export function downloadSeatingCSV(
    assignment: SeatingAssignment,
    chart: SeatingChart,
    students: Student[],
    filename?: string
): void {
    const csv = seatingToCSV(assignment, chart, students);
    const name = filename || `seating-${chart.name}-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadFile(csv, name);
}

/**
 * Download roster as CSV
 */
export function downloadRosterCSV(students: Student[], filename?: string): void {
    const csv = rosterToCSV(students);
    const name = filename || `roster-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadFile(csv, name);
}

/**
 * Generate PDF from groups (uses browser print)
 */
export function printGroupsAsPDF(groups: Group[], students: Student[], title?: string): void {
    const idToStudent = new Map(students.map(s => [s.id, s]));

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
        alert("Please allow popups to print PDF");
        return;
    }

    let html = `
<!DOCTYPE html>
<html>
<head>
    <title>${title || "Groups"}</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 20px; }
        h1 { text-align: center; margin-bottom: 20px; }
        .groups-container { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .group-card { border: 1px solid #ddd; border-radius: 8px; padding: 16px; page-break-inside: avoid; }
        .group-name { font-weight: bold; font-size: 18px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #333; }
        .student { padding: 4px 0; }
        @media print {
            .groups-container { display: block; }
            .group-card { margin-bottom: 16px; }
        }
    </style>
</head>
<body>
    <h1>${title || "Class Groups"}</h1>
    <div class="groups-container">
`;

    for (const group of groups) {
        const groupName = group.name || `Group ${group.id.replace(/^g/, "")}`;
        html += `<div class="group-card" style="background: ${group.color || '#f5f5f5'}">`;
        html += `<div class="group-name">${groupName}</div>`;
        for (const memberId of group.memberIds) {
            const student = idToStudent.get(memberId);
            if (student) {
                html += `<div class="student">${student.name}</div>`;
            }
        }
        html += `</div>`;
    }

    html += `
    </div>
</body>
</html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
        printWindow.print();
    };
}

/**
 * Generate PDF from seating chart (uses browser print)
 */
export function printSeatingAsPDF(
    assignment: SeatingAssignment,
    chart: SeatingChart,
    students: Student[],
    title?: string
): void {
    const idToStudent = new Map(students.map(s => [s.id, s]));

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
        alert("Please allow popups to print PDF");
        return;
    }

    let html = `
<!DOCTYPE html>
<html>
<head>
    <title>${title || chart.name}</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 20px; }
        h1 { text-align: center; margin-bottom: 20px; }
        .tables-container { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .table-card { border: 1px solid #ddd; border-radius: 8px; padding: 16px; page-break-inside: avoid; }
        .table-name { font-weight: bold; font-size: 16px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #ccc; }
        .seat { padding: 4px 8px; margin: 4px 0; background: #f5f5f5; border-radius: 4px; }
        .seat.empty { color: #999; font-style: italic; }
    </style>
</head>
<body>
    <h1>${title || chart.name}</h1>
    <div class="tables-container">
`;

    for (const table of chart.tables) {
        html += `<div class="table-card">`;
        html += `<div class="table-name">${table.name}</div>`;
        for (let i = 0; i < table.seats.length; i++) {
            const seat = table.seats[i];
            if (!seat) continue;
            const studentId = assignment.seatAssignments.get(seat.id);
            const student = studentId ? idToStudent.get(studentId) : null;
            if (student) {
                html += `<div class="seat">Seat ${i + 1}: ${student.name}</div>`;
            } else {
                html += `<div class="seat empty">Seat ${i + 1}: (Empty)</div>`;
            }
        }
        html += `</div>`;
    }

    html += `
    </div>
</body>
</html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
        printWindow.print();
    };
}
